import { Entities, EntityKind } from "../state/entityKinds";
import { GAME_VARIABLES, GameEvents, layerDepthDiff } from "../constants";
import GameState, { getXYindex } from "../state/gameState";
import { Dir } from "./Player";
import { RemovedBecause } from "../state/rules/overlap";
export enum TempState {
  Adding,
  Removing,
  None,
}

export default class BaseEntity {
  protected id: number;
  protected state: GameState;
  protected scene: Phaser.Scene;
  protected tempState: TempState;

  protected idx: number | null;
  constructor(gameState: GameState, scene: Phaser.Scene) {
    this.id = GAME_VARIABLES.OBJ_ID++;
    this.state = gameState;
    this.scene = scene;
    this.tempState = TempState.None;
    this.idx = null;
    const removeFn = () => {
      if (this.tempState === TempState.Removing) {
        this.boundRemovedFromBoard();
      } else if (this.tempState === TempState.Adding) {
        this.boundAddToBoard();
      }
      this.tempState = TempState.None;
    };
    scene.events.on(GameEvents.AddAndRemoveEntities, removeFn, this);
    scene.events.once("destroy", () => {
      scene.events.removeListener(
        GameEvents.AddAndRemoveEntities,
        removeFn,
        this
      );
    });
  }

  public get entityImmutable() {
    if (this.idx === null) {
      return null;
    }
    const entity = this.state.turn_gameState.board[this.idx].find(
      ({ id }) => id === this.id
    );
    if (!entity) {
      throw new Error("Lost reference to entity! Oh no!");
    }
    return entity;
  }

  afterMove() {}

  // Lets us easily and quickly move entities around.
  _moveTween(dir: Dir, opts: any = {}) {
    if (opts.targets) {
      const sprite: Phaser.GameObjects.Sprite = opts.targets;
      this.scene.time.delayedCall(GAME_VARIABLES.MOVE_TIME_MS, () =>
        this.afterMove()
      );
    }
    if (dir === Dir.Up) {
      this.scene.tweens.add({
        ...{
          y: "-=8",
          duration: GAME_VARIABLES.MOVE_TIME_MS,
          repeat: 0,
        },
        ...opts,
      });
    } else if (dir === Dir.Down) {
      this.scene.tweens.add({
        ...{
          y: "+=8",
          duration: GAME_VARIABLES.MOVE_TIME_MS,
          repeat: 0,
        },
        ...opts,
      });
    } else if (dir === Dir.Left) {
      this.scene.tweens.add({
        ...{
          x: "-=8",
          duration: GAME_VARIABLES.MOVE_TIME_MS,
          repeat: 0,
        },
        ...opts,
      });
    } else if (dir === Dir.Right) {
      this.scene.tweens.add({
        ...{
          x: "+=8",
          duration: GAME_VARIABLES.MOVE_TIME_MS,
          repeat: 0,
        },
        ...opts,
      });
    }
  }

  protected _removeFromBoard(reason: RemovedBecause) {
    try {
      console.log("base implementation of removal", this.entityImmutable);
    } catch (_) {
      console.error("base implementation of removal - lost reference");
    }
    this.state.mutateGameState((state) => {
      if (this.idx !== null) {
        const entity_idx = state.board[this.idx as number].findIndex(
          ({ id }) => id === this.id
        );
        state.board[this.idx as number].splice(entity_idx, 1);
      }

      this.idx = null;
    });
    this.afterRemoved(reason);
  }

  /**
   * Base implementations for what happens if you get removed.
   */
  private afterRemoved(reason: RemovedBecause) {
    console.log("after Removed: ", reason);
    const sprite: Phaser.GameObjects.Sprite = (this as any).sprite as any;
    if (
      reason === RemovedBecause.Fallen &&
      (this as any).sprite !== undefined
    ) {
      this.scene.time.delayedCall(GAME_VARIABLES.MOVE_TIME_MS, () => {
        const initialAlpha = sprite.alpha;
        this.afterMove();
        this.scene.add.tween({
          targets: sprite,
          y: "+= 34",
          alpha: { from: initialAlpha, to: 0 },
          ease: "Quad.easeIn",
          duration: 1000,
          onComplete: () => {
            sprite.setVisible(false);
            sprite.setActive(false);
          },
        });
      });
    } else {
      this.scene.time.delayedCall(GAME_VARIABLES.MOVE_TIME_MS - 2, () => {
        this.scene.add.tween({
          targets: sprite,
          alpha: { from: 1, to: 0 },
          repeat: 3,
          duration: 20,
          repeatDelay: 20,
        });
      });
    }
  }

  // Placeholder to be overwritten
  private boundRemovedFromBoard() {
    throw new Error("bound remove method not overwritten");
  }

  removeFromBoard(reason: RemovedBecause) {
    this.tempState = TempState.Removing;
    this.boundRemovedFromBoard = this._removeFromBoard.bind(this, reason);
  }

  // Queue adding the entity to the board.
  addToBoard(e: Entities) {
    this.tempState = TempState.Adding;
    this.boundAddToBoard = this._addToBoard.bind(this, e);
  }

  protected _addToBoard(e: Entities) {
    throw new Error("unimplemented _addToBoard");
  }

  private boundAddToBoard() {
    throw new Error("bound add method not overwritten");
  }

  move(dir?: Dir, isDelayed = false) {
    if (dir) {
      this.moveState(dir);
    }
  }

  moveState(dir: Dir) {
    this.state.mutateGameState((state) => {
      if (this.idx === null) {
        throw new Error("LOST TRACK OF INDEX");
      }
      const entity = state.board[this.idx].find(({ id }) => id === this.id);
      if (!entity) {
        throw new Error("Lost reference to entity! Oh no!");
      }
      if (dir === Dir.Up) {
        entity.y -= 1;
      } else if (dir === Dir.Down) {
        entity.y += 1;
      } else if (dir === Dir.Left) {
        entity.x -= 1;
      } else if (dir === Dir.Right) {
        entity.x += 1;
      }
      const last_idx = this.idx;
      this.idx = getXYindex(entity.x, entity.y);
      GameState.moveEntityOnBoard(state, entity, this.idx, last_idx);
    });
  }

  undo(c: Entities) {
    console.warn("undo not implemented for some type");
  }
}
